import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { CalendarDays, ClipboardList, ExternalLink, LayoutDashboard, LogOut, Menu, Mountain, Settings, Wallet } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/admin/pesanan", label: "Pesanan", icon: ClipboardList, id: "pesanan" },
  { to: "/admin/jadwal", label: "Jadwal", icon: CalendarDays, id: "jadwal" },
  { to: "/admin/trip", label: "Trip & Harga", icon: Mountain, id: "trip" },
  { to: "/admin/keuangan", label: "Keuangan", icon: Wallet, id: "keuangan" },
  { to: "/admin/pengaturan", label: "Pengaturan", icon: Settings, id: "pengaturan" },
];

const NavItems = ({ onNavigate, prefix = "admin-nav" }) =>
  NAV.map(({ to, label, icon: Icon, end, id }) => (
    <NavLink
      key={id}
      to={to}
      end={end}
      onClick={onNavigate}
      data-testid={`${prefix}-${id}`}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-gold/10 text-gold" : "text-neutral-400 hover:bg-white/5 hover:text-white"}`
      }
    >
      <Icon className="h-4 w-4" />
      {label}
    </NavLink>
  ));

const SidebarBody = ({ onNavigate, prefix }) => {
  const { user, logout } = useAuth();
  return (
    <div className="flex h-full flex-col">
      <div>
        <p className="text-[9px] font-semibold uppercase tracking-[0.4em] text-gold">Panel Admin</p>
        <p className="mt-1 font-display text-2xl italic">Siput Gunung</p>
      </div>
      <nav className="mt-10 flex-1 space-y-1"><NavItems onNavigate={onNavigate} prefix={prefix} /></nav>
      <div className="space-y-3 border-t border-white/10 pt-5">
        <Link to="/" target="_blank" className="flex items-center gap-2 text-xs text-neutral-500 hover:text-white" data-testid={`${prefix}-view-site`}>
          <ExternalLink className="h-3.5 w-3.5" /> Lihat website
        </Link>
        <p className="truncate text-xs text-neutral-500">{user?.email}</p>
        <button type="button" onClick={logout} className="flex items-center gap-2 text-sm text-neutral-300 hover:text-rose-300" data-testid={`${prefix}-logout`}>
          <LogOut className="h-4 w-4" /> Keluar
        </button>
      </div>
    </div>
  );
};

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-ink-950 text-neutral-100 lg:flex">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-r border-white/10 bg-black/40 p-6 lg:block">
        <SidebarBody prefix="admin-nav" />
      </aside>
      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-white/10 bg-black/80 px-4 backdrop-blur-xl lg:hidden">
          <p className="font-display text-xl italic">Siput Gunung</p>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15" data-testid="admin-mobile-menu-btn">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 border-white/10 bg-ink-950 p-6">
              <SheetTitle className="sr-only">Menu admin</SheetTitle>
              <SidebarBody onNavigate={() => setOpen(false)} prefix="admin-mobile-nav" />
            </SheetContent>
          </Sheet>
        </header>
        <main className="mx-auto max-w-[1400px] p-4 sm:p-6 lg:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
